import * as _ from "lodash";

import type Light from "@/models/light/light";
import type { MarkerPosition } from "@/models/marker-position";
import { Map } from "@/utils";

/**
 * Determines whether a light is "Not Commissioned" based on its positioning IDs
 * and the floor space's positioning type.
 *
 * @param {Light} light - The light to check for commissioning status.
 * @returns {boolean} Whether the light is not commissioned for the given positioning type.
 */
export const isNotCommissioned = (light: Light): boolean => {
    const vlcId = _.toNumber(light.id);
    const bleId = _.toNumber(light.bleId);
    const { positioningType } = light;

    return (
        (vlcId === 0 &&
            (positioningType === "VLC" || positioningType === "VLC + BLE")) ||
        (bleId === 0 && positioningType === "BLE")
    );
};

/* Rotate Lights...*/

/**
 * Rotates all lights, group geometries, and markers by the given degrees around
 * the origin. Markers are repositioned using the same transformation as geometry
 * vertices so they maintain their relative positioning.
 *
 * @param lights - The array of light models to rotate.
 * @param geometries - The group geometries to rotate.
 * @param rotationDegrees - The rotation angle in degrees.
 * @param markers - Optional array of marker positions to rotate.
 * @returns An object containing the rotated lights, geometries, and markers.
 */
export const rotateLights = (
    lights: unknown[],
    geometries: { vertices: { lat: number; lng: number }[] }[],
    rotationDegrees = -90,
    markers: MarkerPosition[] = [],
) => {
    const rotateRadians = rotationDegrees * (Math.PI / 180);
    const newOriginX = 0;
    const newOriginY = 0;

    const rotateX = (x: number, y: number) => {
        const translateX = x - newOriginX;
        const translateY = y - newOriginY;

        return (
            translateX * Math.cos(rotateRadians) -
            translateY * Math.sin(rotateRadians) +
            newOriginX
        );
    };

    const rotateY = (x: number, y: number) => {
        const translateX = x - newOriginX;
        const translateY = y - newOriginY;

        return (
            translateY * Math.cos(rotateRadians) +
            translateX * Math.sin(rotateRadians) +
            newOriginY
        );
    };

    let overallMaxX = Number.NEGATIVE_INFINITY;
    let overallMaxY = Number.NEGATIVE_INFINITY;

    let l = lights.map((lightModel: any) => {
        const light = lightModel.convertToAPI();
        light.uniqueId = lightModel.uniqueId;
        const minX = Math.min(Math.min(light.x1, light.x2), light.x3);
        const maxX = Math.max(Math.max(light.x1, light.x2), light.x3);
        const minY = Math.min(Math.min(light.y1, light.y2), light.y3);
        const maxY = Math.max(Math.max(light.y1, light.y2), light.y3);

        const x1 = rotateX(minX, minY);
        const x2 = rotateX(maxX, maxY);
        const y1 = rotateY(minX, minY);
        const y2 = rotateY(maxX, maxY);

        light.x1 = Math.max(x1, x2);
        light.y1 = Math.max(y1, y2);

        light.x2 = Math.max(x1, x2);
        light.y2 = Math.min(y1, y2);

        light.x3 = Math.min(x1, x2);
        light.y3 = Math.min(y1, y2);

        const temp = light.externalWidth;
        light.externalWidth = light.externalLength;
        light.externalLength = temp;

        overallMaxX = Math.max(overallMaxX, Math.max(x1, x2));
        overallMaxY = Math.max(overallMaxY, Math.max(y1, y2));
        return light;
    });

    let g = geometries.map((geometry) => {
        const vertices = geometry.vertices.map(({ lat, lng }) => {
            const newX = -rotateX(lng, lat);
            const newY = -rotateY(lng, lat);

            overallMaxX = Math.max(overallMaxX, newX);
            overallMaxY = Math.max(overallMaxY, newY);

            return { lat: newY, lng: newX };
        });

        return { ...geometry, vertices };
    });

    let m = markers.map((marker) => {
        const newX = -rotateX(marker.x, marker.y);
        const newY = -rotateY(marker.x, marker.y);

        overallMaxX = Math.max(overallMaxX, newX);
        overallMaxY = Math.max(overallMaxY, newY);

        return { ...marker, x: newX, y: newY };
    });

    l = l.map((light) => {
        light.x1 -= overallMaxX;
        light.x2 -= overallMaxX;
        light.x3 -= overallMaxX;
        light.y1 -= overallMaxY;
        light.y2 -= overallMaxY;
        light.y3 -= overallMaxY;
        return Map.Converter.lights.APIToViewModel(light);
    });

    g = g.map((geometry) => ({
        ...geometry,
        vertices: geometry.vertices.map(({ lat, lng }) => ({
            lat: -(lat - overallMaxY),
            lng: -(lng - overallMaxX),
        })),
    }));

    m = m.map((marker) => ({
        ...marker,
        x: -(marker.x - overallMaxX),
        y: -(marker.y - overallMaxY),
    }));

    return { lights: l, geometries: g, markers: m };
};
